import { useState } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../ui/Card';
import Button from '../ui/Button';

/**
 * Poll Results Component
 * Real-time visualization of poll results with bar and pie charts
 */

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const PollResults = ({ activity, responses = [], showProjection = false, onExport }) => {
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'pie'

  // Get options array (can come from activity.options or activity.config.options)
  const getOptions = () => {
    const opts = activity?.options || activity?.config?.options || [];
    // Normalize to array of { text, id } objects
    return opts.map((opt, index) => ({
      text: typeof opt === 'string' ? opt : opt.text || opt,
      id: typeof opt === 'string' ? index : (opt.id !== undefined ? opt.id : index),
    }));
  };

  // Calculate results from responses
  const calculateResults = () => {
    const options = getOptions();
    if (options.length === 0) return [];

    const counts = options.map((option, index) => {
      // Handle different response formats:
      // 1. { value: index } or { value: [indices] } - index-based (from PollActivity)
      // 2. { answer: id } or { answer: [ids] } - id-based (legacy format)
      // 3. { type: 'poll', value: index } - wrapped format from WebSocket
      const count = responses.filter((r) => {
        // Get the actual response value
        const value = r?.value !== undefined ? r.value : r?.answer;

        if (Array.isArray(value)) {
          return value.includes(index) || value.includes(option.id);
        }
        return value === index || value === option.id;
      }).length;

      return {
        name: option.text,
        value: count,
        percentage: responses.length > 0 ? ((count / responses.length) * 100).toFixed(1) : 0,
      };
    });

    return counts;
  };

  const results = calculateResults();
  const totalResponses = responses.length;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-neutral-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-neutral-900">{payload[0].name}</p>
          <p className="text-sm text-neutral-600">
            Votes: <span className="font-semibold text-primary-600">{payload[0].value}</span>
          </p>
          <p className="text-sm text-neutral-600">
            Percentage: <span className="font-semibold text-primary-600">{payload[0].payload.percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percentage < 5) return null; // Don't show label if too small

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="font-semibold text-sm">
        {`${percentage}%`}
      </text>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card padding="lg" className="border border-neutral-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">{activity?.title || 'Poll Results'}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-neutral-500">
                {totalResponses} {totalResponses === 1 ? 'response' : 'responses'}
              </span>
              {(activity?.allowMultiple || activity?.config?.pollType === 'multiple') && (
                <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded">
                  Multiple Choice
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Chart Type Toggle */}
            <div className="flex bg-neutral-100 rounded-lg p-1">
              <button
                onClick={() => setChartType('bar')}
                className={`p-2 rounded text-sm ${
                  chartType === 'bar'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                title="Bar Chart"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`p-2 rounded text-sm ${
                  chartType === 'pie'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                title="Pie Chart"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </button>
            </div>

            {/* Export Button */}
            {onExport && (
              <Button variant="ghost" size="sm" onClick={() => onExport(results, 'poll')}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            )}
          </div>
        </div>

        {/* Chart Display */}
        {totalResponses > 0 ? (
          <div className="w-full" style={{ height: '400px' }}>
            {chartType === 'bar' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="value" name="Votes" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                    {results.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={results}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={800}
                  >
                    {results.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-neutral-50 rounded-lg">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-neutral-600">No responses yet</p>
              <p className="text-sm text-neutral-500">Results will appear as participants vote</p>
            </div>
          </div>
        )}
      </Card>

      {/* Projection Mode Indicator */}
      {showProjection && (
        <div className="fixed top-4 right-4 bg-neutral-800 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>Projecting</span>
        </div>
      )}
    </div>
  );
};

export default PollResults;
